import {callWithNuxt} from 'nuxt/app';
import {getQuery} from 'ufo';
import type {RouteLocationNormalized} from 'vue-router';
import {defineNuxtRouteMiddleware, createError, useGuestHost, useAuth, useNuxtApp, useRuntimeConfig} from '#imports';
import {safelyNavigateTo} from '@malt/utils';
import {redirectToWhenUnlogged} from '../internals/redirectToWhenUnlogged';
import type {RequiredRoles} from '../models/RequiredRoles';
import type {UserIdentityType} from '../models/User';
import {redirectToSignIn} from '../utils/redirectToSignin';
import type {Logger} from '#malt/nuxt-logger-module';

/**
 * Here, we rely on the store to check if the user is logged in and allow them to navigate to the
 * destination page.
 * A page will have to include this middleware to protect it from unauthorized access.
 *
 * This middleware also takes care of enforcing a user identity, and of redirecting the user elsewhere
 * in case some workflow is in progress, for example in case a client signup.
 * Note that redirection to a workflow in progress can also happen for logged out users, in which case
 * redirect-user-to-workflow-in-progress.global.ts takes care of it.
 */
export default defineNuxtRouteMiddleware(async (to, from) => {
    const nuxtApp = useNuxtApp();
    const {$pinia} = nuxtApp;

    const baseURL = useRuntimeConfig().app.baseURL;

    const store = useAuth($pinia);
    const host = useGuestHost();

    function shouldRedirectUserTo(to: RouteLocationNormalized, pathWhereToRedirectUser: string) {
        const currentPath = baseURL === '/' ? to.path : baseURL + to.path;

        return currentPath !== pathWhereToRedirectUser && (pathWhereToRedirectUser === '/' || !currentPath.startsWith(pathWhereToRedirectUser));
    }

    const currentPath = baseURL === '/' ? to.path : baseURL + to.path;
    const queryParams = getQuery(to.fullPath);
    // Cover the error `logger is undefined`
    const logger = nuxtApp.$logger as Logger;

    if (to.meta.enforcedIdentity != null) {
        const enforcedIdentitySpec = to.meta.enforcedIdentity(to);

        let isLogged: boolean, hasValidIdentity: boolean, pathWhereToRedirectUser: string | undefined;
        try {
            ({isLogged, hasValidIdentity, pathWhereToRedirectUser} = await callWithNuxt(nuxtApp, () =>
                store.tryToEnforceIdentity(enforcedIdentitySpec, to.meta.allowAdminIfNoEnforcedIdentity),
            ));

            if (pathWhereToRedirectUser && shouldRedirectUserTo(to, pathWhereToRedirectUser)) {
                return callWithNuxt(nuxtApp, () => safelyNavigateTo({path: pathWhereToRedirectUser}, {externalToThisApp: true}));
            }
        } catch (e) {
            logger.error(`Failed to enforce identity, ${e}`, e);
            return callWithNuxt(nuxtApp, redirectToSignIn);
        }

        if (!isLogged) {
            if (to.meta.allowUnloggedIfNoEnforcedIdentity) {
                return;
            }

            logger.info(`Cannot enforce identity for unlogged user`);
            return callWithNuxt(nuxtApp, redirectToSignIn);
        }

        if (!hasValidIdentity && !to.meta.allowAccessIfNoEnforcedIdentity) {
            if (to.meta.identityRedirect) {
                const redirectUrl = `${host}${
                    to.meta.identityRedirect instanceof Function ? to.meta.identityRedirect(to) : to.meta.identityRedirect
                }`;

                return callWithNuxt(nuxtApp, () => safelyNavigateTo(redirectUrl, {externalToThisApp: true}));
            }

            throw createError({statusCode: 403, statusMessage: 'Invalid identity'});
        }

        if (store.currentIdentityIsUndefined && to.meta.forceQualificationOfUndefinedIdentity && !currentPath.startsWith('/account/membership')) {
            return callWithNuxt(nuxtApp, () => safelyNavigateTo({path: '/account/membership'}, {externalToThisApp: true}));
        }
    }

    try {
        const requiredRoles: RequiredRoles = to.meta.roles ?? [];
        const requiredIdentity: UserIdentityType | 'ANY' = to.meta.identity ?? 'ANY';
        const allowImmersion = to.meta.allowImmersion ?? false;
        const forbidAccessIfCompanyIsSuspended = to.meta.forbidAccessIfCompanyIsSuspended ?? false;

        const {isLogged, pathWhereToRedirectUser} = await callWithNuxt(nuxtApp, store.checkIfLoggedOrIfNeedingRedirect);

        const hasAdminRole = store?.user?.roles?.includes('ROLE_ADMIN');

        // FIXME there is an implicit dependency with the implementation of the immersion mode here
        //  it should probably be refactored as an explicit parameter provided by the "immersion-mode" middleware
        const isInImmersion = queryParams.identityId !== undefined || queryParams.immersion !== undefined;
        const canBypassIdentityRedirection = allowImmersion && hasAdminRole && isInImmersion;

        if (pathWhereToRedirectUser && shouldRedirectUserTo(to, pathWhereToRedirectUser)) {
            return callWithNuxt(nuxtApp, () => safelyNavigateTo({path: pathWhereToRedirectUser}, {externalToThisApp: true}));
        }

        if (!isLogged) {
            if (to.query?.['x-auth']) {
                // In this case authentication will be handled by the page
                return;
            }
            if (to.meta.redirectToWhenUnlogged) {
                return redirectToWhenUnlogged(to, from);
            }
            return callWithNuxt(nuxtApp, redirectToSignIn);
        } else if (forbidAccessIfCompanyIsSuspended && store.isCompanySuspended) {
            return callWithNuxt(nuxtApp, () => safelyNavigateTo('/client-settings/restricted', {externalToThisApp: true}));
        } else if (
            store.currentIdentityIsUndefined &&
            to.meta.forceQualificationOfUndefinedIdentity &&
            !currentPath.startsWith('/account/membership')
        ) {
            return callWithNuxt(nuxtApp, () => safelyNavigateTo({path: '/account/membership'}, {externalToThisApp: true}));
        } else if (!store.hasEnoughPrivilege(requiredRoles)) {
            return callWithNuxt(nuxtApp, () => safelyNavigateTo(host, {externalToThisApp: true}));
        } else if (
            queryParams.selectedIdentityId &&
            typeof queryParams.selectedIdentityId === 'string' &&
            queryParams.selectedIdentityId !== store.getSelectedIdentity?.id
        ) {
            try {
                await callWithNuxt(nuxtApp, () =>
                    store.tryToEnforceIdentity({
                        strategy: 'having-id',
                        parameters: {
                            identityId: String(queryParams.selectedIdentityId),
                        },
                    }),
                );
            } catch (e) {
                logger.error(`Failed to switch identity to ${queryParams.selectedIdentityId}, ${e}`, e);
                return callWithNuxt(nuxtApp, redirectToSignIn);
            }
        } else if (!store.hasCorrectIdentity(requiredIdentity) && !canBypassIdentityRedirection) {
            if (to.meta.identityRedirect instanceof Function) {
                const redirectUrl = `${host}${to.meta.identityRedirect(to)}`;
                return callWithNuxt(nuxtApp, () => safelyNavigateTo(redirectUrl, {externalToThisApp: true}));
            }
            const redirectUrl = `${host}${to.meta.identityRedirect || ''}`;
            return callWithNuxt(nuxtApp, () => safelyNavigateTo(redirectUrl, {externalToThisApp: true}));
        }
    } catch (e) {
        logger.error(`Failed to check if logged or needing redirect ${e}`, e);
        return callWithNuxt(nuxtApp, redirectToSignIn);
    }
});
